import { ImageName } from "@mcleod/images";
import { CommonDialogs } from "@mcleod/common";
import {
    BlurEvent, ChangeEvent,
    Checkbox,
    ClickEvent,
    Component,
    CrudDecorator,
    DataSource,
    DataSourceMode,
    DropdownItem,
    Label,
    Layout,
    Location,
    LookupModelSelectionEvent,
    RouteType,
    Snackbar,
    TableAction,
    TableContentsChangedEvent,
    TableRow,
    TableRowCreationEvent,
    TableRowCreationListener,
    TableRowDisplayEvent,
    TableRowMode,
    TableRowMoveEvent, Textbox
} from "@mcleod/components";
import { getDispatchControlBoolean, LMEUserSettings } from "@mcleod/general";
import { Api, DisplayType, Keys, LogManager, MapSettings, StringUtil } from "@mcleod/core";
import { QuickQuote } from "@mcleod/powerbroker";
import { DistanceCalculatorOverview } from "./DistanceCalculatorOverview";
import { DistanceRateUtil } from "./DistanceRateUtil";
import { RateIndexDataSummary } from "./RateIndexDataSummary";
import { AutogenLayoutDistanceRateIndexCalculator } from "./autogen/AutogenLayoutDistanceRateIndexCalculator";
import { DistanceRouteDetails } from "./DistanceRouteDetails";

const GoogleMapVendor = 'G';
const TrimbleAlkMapVendor = 'W';
const PCMILER_ON_PREM = 'A';
const MULTI_VERSION_PCMILER = 'M';
const MILEMAKER = 'E';
const PCMILER_WEB = "T";

const HAZMAT_NONE = 0;
const HAZMAT_GENERAL = 1;

const EQUIPMENT_TYPE_VAN = new DropdownItem("V", "Van");
const EQUIPMENT_TYPE_FLAT = new DropdownItem("F", "Flat");
const EQUIPMENT_TYPE_REEFER = new DropdownItem("R", "Reefer");
const EQUIPMENT_TYPE_OTHER = new DropdownItem("O", "Other");

const log = LogManager.getLogger("dispatch.DistanceRateIndexCalculator");

export enum DISTANCE_PROFILE {
    BILLING = "BILLING",
    FUEL = "FUEL",
    PAY = "PAY",
    PRACTICL = "PRACTICL",
    SHORT = "SHORT"
}

export enum RATE_VENDORS {
    MPACT = "E",
    DAT = "T",
    TRUCKSTOP = "I",
    FREIGHT_WAVES = "F",
    GREENSCREENS = "G"
}

const CUSTOM_PRAC_PROFILE = "CustomPract";
const CUST_SHORT_PROFILE = "CustomShort";

export class DistanceRateIndexCalculator extends AutogenLayoutDistanceRateIndexCalculator {
    private distCalcMapVendor: string = MapSettings.getSingleton().getDistanceCalcVendor();
    private isPcMilerOnPremVendor: boolean;
    private isPcMilerWebDistanceVendor: boolean;
    private isMileMakerVendor: boolean;
    private _lastMapResponse: any = null;
    private _lastMapRouteData: any[] = null;
    private _showAllTotalRates: boolean = false;
    private currentRateIndexIdMap;
    private _forceRefresh = true;
    private distanceUtil = new DistanceRateUtil();
    private _cachedShortProfile;
    private _cachedPracticalProfile;
    private _cachedTrailerType;
    private rowCreationListener: TableRowCreationListener;
    private _userMarginPercentage = LMEUserSettings.get()?.margin_percentage;
    private rateRetrievalList: string[] = [];
    private _displayDATSpotRate: boolean = true;
    private _displayDATContractRate: boolean = true;
    private _displayTruckstopPaidRate: boolean = true;
    private _displayTruckstopPostedRate: boolean = true;
    private _displayGreenscreensTargetRate: boolean = true;
    private _displayGreenscreensNetworkRate: boolean = true;
    private _displayMPactSellRate: boolean = true;
    private _displayMPactSpotRate: boolean = true;
    private _displayMPactInternalRate: boolean = true;

    private _timeFrameContractOptions: DropdownItem[] = [
        new DropdownItem("30D", "30 Days"),
        new DropdownItem("60D", "60 Days"),
        new DropdownItem("90D", "90 Days"),
        new DropdownItem("365D", "365 Days")
    ];

    private _timeFrameSpotOptions: DropdownItem[] = [
        new DropdownItem("3D", "3 Days"),
        new DropdownItem("7D", "7 Days"),
        new DropdownItem("15D", "15 Days"),
        new DropdownItem("30D", "30 Days"),
        new DropdownItem("60D", "60 Days"),
        new DropdownItem("90D", "90 Days")
    ];

    private _escalationTypeOptions: DropdownItem[] = [
        new DropdownItem('B', 'Best Fit'),
        new DropdownItem('S', 'Specific Area Type And Specific Time Frame'),
        new DropdownItem('M', 'Minimum Area Type And Minimum Time Frame'),
        new DropdownItem('SS', 'Specific Area Type And Shortest Time Frame'),
        new DropdownItem('SA', 'Specific Time Frame And Smallest Area Type')
    ];
    private _areaTypeOptions: DropdownItem[] = [
        new DropdownItem("3DZ", "3 Digit Zip"),
        new DropdownItem("MA", "Market Area"),
        new DropdownItem("EMA", "Extended Market Area"),
        new DropdownItem("R", "Region")
    ];
    private _areaType: string;
    private _timeFrameSpot: string;
    private _timeFrameContract: string;
    private _isSpotRate: boolean;
    private _isContractRate: boolean;
    private _escalationType: string;

    override onLoad(): void {
        this.initializeStopTable();
        this.textboxTrailerType.items = [EQUIPMENT_TYPE_VAN, EQUIPMENT_TYPE_FLAT, EQUIPMENT_TYPE_REEFER, EQUIPMENT_TYPE_OTHER];
        this.textboxTrailerType.setSelectedItemFromValue(this.textboxTrailerType.items[0].value);
        this.textboxMarginPercentage.text = this._userMarginPercentage;
        this.sourceStops.parentDataSource = new DataSource();
        this.tableStops.data = [];
        this.initializeFieldsViaRateIndexControl();
        this.onSelectPracticalProfile();
        this.onSelectShortDistProfile();
        this.initializeChangeListeners()
        this.addLayoutDataLoadListener(() => {
            this.showMap();
        });
        this.mapRoute.createMapCallback = () => {
            if (this._lastMapResponse != null && this._lastMapRouteData != null) {
                this.populateMap(this._lastMapResponse, this.cloneRouteDataForMap(this._lastMapRouteData));
            }
        };
        this.panelDistanceCalculator.addHandlerForKey(Keys.ENTER, { ctrlKey: true }, () => this.buttonCalculateOnClick());
        this.panelDistanceCalculator.addHandlerForKey(Keys.ENTER, null, () => this.buttonCalculateOnClick());
        this.currentRateIndexIdMap = new Map<String, String>();

        this.rowCreationListener = (event: TableRowCreationEvent) => {
            const tableRow = event.getTableRow();
            if (tableRow.mode == TableRowMode.QUICK_ADD) {
                tableRow.addMountListener(() => {
                    this.focusLocation(tableRow);
                    if (this.calculationDisplayed) {
                        this.buttonCalculateOnClick();
                    }
                });
            }
        }
        this.buttonQuote.enabled = false;
    }

    get calculationDisplayed(): boolean {
        return this.buttonRouteDetails.enabled;
    }

    private onSelectShortDistProfile() {
        const layoutDistanceCalculator = this.layoutDistanceCalculationOverview as DistanceCalculatorOverview;
        layoutDistanceCalculator.textboxShortDistProfile.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                if (this.currentRateIndexIdMap != undefined && this.currentRateIndexIdMap.size != 0 && (event.selection as any).caption != this.cachedShortProfile) {
                    this.currentRateIndexIdMap.clear();
                }
                this.buttonFetchVendorResultsOnClick(this._showAllTotalRates, false);
            }
        });
    }

    private onSelectPracticalProfile() {
        const layoutDistanceCalculator = this.layoutDistanceCalculationOverview as DistanceCalculatorOverview;
        layoutDistanceCalculator.textboxPracticalProfile.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                if (this.currentRateIndexIdMap != undefined && this.currentRateIndexIdMap.size != 0 && (event.selection as any).caption != this.cachedPracticalProfile) {
                    this.currentRateIndexIdMap.clear();
                }
                this.buttonFetchVendorResultsOnClick(this._showAllTotalRates, false);
            }
        });
    }

    textboxTrailerTypeOnChange() {
        if (this.currentRateIndexIdMap != undefined && this.currentRateIndexIdMap.size != 0 && this.textboxTrailerType.text != this.cachedTrailerType) {
            this.currentRateIndexIdMap.clear();
        }

        if (this.calculationDisplayed) {
            this.buttonCalculateOnClick();
        }
    }

    private initializeChangeListeners() {
        const layoutDistanceCalculator = this.layoutDistanceCalculationOverview as DistanceCalculatorOverview;

        // Add listeners to other relevant components in layoutDistanceCalculator
        layoutDistanceCalculator.getRecursiveChildren().forEach((component: Component) => {
            if (component instanceof Checkbox || component instanceof Textbox) {
                component.addChangeListener(() => this.disableRouteDetailsButtonAndResetMileageCalculation(layoutDistanceCalculator));
            }
        });
    }

    private disableRouteDetailsButtonAndResetMileageCalculation(layoutDistanceCalculator: DistanceCalculatorOverview) {
        // Reset labels in layoutDistanceCalculator
        layoutDistanceCalculator.labelShortMilesCalculateValue.text = "---";
        layoutDistanceCalculator.labelShortTollAmntCalculateValue.text = "---";
        layoutDistanceCalculator.labelShortTollAmntCalculateValue.color = "#252525";
        layoutDistanceCalculator.labelPracticalMilesCalculateValue.text = "---";
        layoutDistanceCalculator.labelPracticalTollAmntCalculateValue.text = "---";
        layoutDistanceCalculator.labelPracticalTollAmntCalculateValue.color = "#252525";

        // Clear the map route
        this.resetMap();

        // Disable the Route Details button
        this.buttonRouteDetails.enabled = false;

        // Disable the Get Other Rates button
        this.buttonFetchOtherResults.enabled = false;

        // Reset layoutTotalRatesSummary
        const layoutTotalRatesSummary = this.layoutTotalRatesSummary as RateIndexDataSummary;
        layoutTotalRatesSummary.setDefaultValues();
        this.clearTotalRatesSummaryLayout(layoutTotalRatesSummary);
        this.enableButtons(true);
    }

    /** When Screen loads By Default add two Stop */
    private async initializeStopTable() {
        this.tableStops.removeRowCreateListener(this.rowCreationListener);
        await this.addNewStopToTable();
        await this.addNewStopToTable();
        this.focusLocation(this.tableStops.rows[0]);
        this.tableStops.addRowCreateListener(this.rowCreationListener);
    }

    private async addNewStopToTable() {
        const newRowData = await this.tableStops._createNewRowData();
        if (newRowData != null) {
            this.tableStops.addRow(newRowData, { mode: TableRowMode.ADD }, { display: true, addToData: true });
        }
    }

    private validateStops() {
        let componentPassedValidation = false;
        if (this.tableStops.rowCount < 2) {
            CommonDialogs.showError("At least two stops are required to calculate the Short and Practical (Mileage and Toll Amount).");
            return;
        }
        else if (this.tableStops.rowCount > 1) {
            componentPassedValidation = this.tableStops.validateSimple(true, true);
            if (!componentPassedValidation) {
                Snackbar.showWarningSnackbar("All stops must have locations entered.");
                this.buttonQuote.enabled = false;
                return componentPassedValidation;
            }
            return componentPassedValidation;
        }
    }

    private async calculate(routeData: any[], fetchOtherResults: boolean, rateRetrievalList: any[]) {
        const equipmentId = this.setEquipmentId();
        const marginPercentage = this.setMarginPercentage();

        if (marginPercentage == null || marginPercentage == "")
            this.textboxMarginPercentage.text = "0";

        const excludeFuelSurcharge = this.switchExcludeFuelSurcharge.checked;
        const layoutDistanceCalculator = this.layoutDistanceCalculationOverview as DistanceCalculatorOverview;
        const totalRatesSummary = this.layoutTotalRatesSummary as RateIndexDataSummary;
        if (layoutDistanceCalculator.sourceDistanceControl.activeRow === undefined) {
            CommonDialogs.showError("Lookup Model cannot be null or empty");
            return;
        }
        const custShortDistOptions = this.layoutDistanceCalculationOverview.isShortConfigExpanded();
        const custPractDistOptions = this.layoutDistanceCalculationOverview.isPracticalConfigExpanded();
        const lookupShortProfile = custShortDistOptions ? CUST_SHORT_PROFILE : layoutDistanceCalculator.sourceDistanceControl.activeRow?.get("short_dist_profile");
        const lookupPracticalProfile = custPractDistOptions ? CUSTOM_PRAC_PROFILE : layoutDistanceCalculator.sourceDistanceControl.activeRow?.get("prac_dist_profile");
        this.cachedShortProfile = lookupShortProfile;
        this.cachedPracticalProfile = lookupPracticalProfile;
        this.cachedTrailerType = this.textboxTrailerType.text;

        const layoutShortDistance = this.layoutDistanceCalculationOverview.layoutShortDistanceRateConfig;
        const layoutPractDistance = this.layoutDistanceCalculationOverview.layoutPracticalDistanceRateConfig;
        this.validationLookUpModel(lookupShortProfile, lookupPracticalProfile);
        const isStopsValid: boolean = this.validateStops();
        // Do not allow calculations if server and lookups are missing
        const isCustShortValid: boolean = this.validateCustShortDistOptions(lookupShortProfile);
        const isCustPractValid: boolean = this.validateCustPractDistOptions(lookupPracticalProfile);
        this.resetMap();
        if (isStopsValid && isCustShortValid && isCustPractValid) {
            this.mainDataSource
                .search({ stops: routeData, short_dist_profile: lookupShortProfile, prac_dist_profile: lookupPracticalProfile, short_dist_options: layoutShortDistance.sourceDistanceProfile.data, pract_dist_options: layoutPractDistance.sourceDistanceProfile.data })
                .then(() => {
                    const response = this.mainDataSource.data[0]?.data;
                    this.whenResponseNull(response, fetchOtherResults);

                    if (response != null) {
                        this._lastMapResponse = response;
                        this._lastMapRouteData = this.cloneRouteDataForMap(routeData);
                        this.showMilesAndTollAmount(response, layoutDistanceCalculator);
                        this.getTotalRatesVendor(routeData, equipmentId, fetchOtherResults, totalRatesSummary, response?.practical_mileage, marginPercentage, excludeFuelSurcharge, response?.practical_toll_amount.amount, lookupPracticalProfile, rateRetrievalList);

                        this.populateMap(response, this.cloneRouteDataForMap(this._lastMapRouteData));
                        this.buttonQuote.enabled = true;
                        this.clearStops.enabled = true;
                        this.clearStops.color = "primary";
                        this.buttonFetchOtherResults.enabled = true;
                        this.buttonFetchOtherResults.color = "primary";
                        this.tableStops.enabled = true;
                        this.buttonRouteDetails.enabled = true;
                    }

                });
        } else {
            this.resetButtons();
        }
    }

    private validateCustPractDistOptions(lookupPracticalProfile: any): boolean {
        if (lookupPracticalProfile !== CUSTOM_PRAC_PROFILE) {
            return true;
        }

        const layout = this.layoutDistanceCalculationOverview.layoutPracticalDistanceRateConfig;
        if (!layout.validateSimple()) {
            Snackbar.showSnackbar("Please enter all required fields");
            return false;
        }

        return true;
    }

    private validateCustShortDistOptions(lookupShortProfile: any): boolean {
        if (lookupShortProfile !== CUST_SHORT_PROFILE) {
            return true;
        }

        const layout = this.layoutDistanceCalculationOverview.layoutShortDistanceRateConfig;
        if (!layout.validateSimple()) {
            Snackbar.showSnackbar("Please enter all required fields");
            return false;
        }

        return true;
    }

    private getTotalRatesVendor(routeData: any[], equipmentId: string, fetchOtherResults: boolean, layoutTotalRates: RateIndexDataSummary, mileage: string, marginPercentage: string, excludeFuelSurcharge: boolean, tolls: string, profile: string, rateRetrievalList: any[]) {
        if (this._forceRefresh)
            layoutTotalRates.hideColumns();

        this.fetchAllRates(routeData, equipmentId, fetchOtherResults, layoutTotalRates, mileage, marginPercentage, excludeFuelSurcharge, tolls, profile, rateRetrievalList, this._escalationType, this._timeFrameSpot, this._timeFrameContract, this._areaType)
            .then(() => {
                this.resetButtons();
            });
    }


    async fetchAllRates(routeData: any[], equipmentId: string, fetchOtherResults: boolean, layoutTotalRates: RateIndexDataSummary, mileage: string, marginPercentage: string, excludeFuelSurcharge: boolean, tolls: string, profile: string, rateRetrievalList: any[], escalationType: string, timeFrameSpot: string, timeFrameContract: string, areaType: string): Promise<void> {
        let vendorList = [];
        const failedVendors = [];

        // Map vendor codes to display names for better messages
        const vendorDisplayNames = {
            [RATE_VENDORS.TRUCKSTOP]: "Truckstop",
            [RATE_VENDORS.DAT]: "DAT",
            [RATE_VENDORS.MPACT]: "MPact",
            [RATE_VENDORS.FREIGHT_WAVES]: "SONAR",
            [RATE_VENDORS.GREENSCREENS]: "GreenScreens"
        };

        if (this.getShowAllRatesBoolean) {
            vendorList = [
                RATE_VENDORS.TRUCKSTOP,
                RATE_VENDORS.DAT,
                RATE_VENDORS.MPACT,
                RATE_VENDORS.FREIGHT_WAVES,
                RATE_VENDORS.GREENSCREENS
            ];
        } else if (rateRetrievalList && rateRetrievalList.length > 0) {
            vendorList = rateRetrievalList;
        }

        // Create array of promises with vendor information
        const vendorPromises = vendorList.map(async vendor => {
            const success = await fetchRateIndexResults(
                this, vendor, routeData, equipmentId,
                this.getShowAllRatesBoolean ? fetchOtherResults : true,
                layoutTotalRates, mileage, marginPercentage, excludeFuelSurcharge,
                tolls, profile, this.currentRateIndexIdMap.get(vendor) ?? null,
                this._forceRefresh, escalationType, timeFrameSpot, timeFrameContract, areaType
            );
            // Track failed vendors
            if (!success) {
                failedVendors.push(vendor);
            }
            return success;
        });

        // Wait for all requests to complete
        await Promise.all(vendorPromises);

        // Show appropriate message based on results
        if(vendorList.length === 0) {
            Snackbar.showWarningSnackbar("No vendors set up for rate retrieval.");
        }else if (vendorList.length > 0) {
            if (failedVendors.length === vendorList.length) {
                // All vendors failed
                Snackbar.showWarningSnackbar("No rate data available from any vendor.");
            } else if (failedVendors.length > 0) {
                // Some vendors failed - list them by name
                const failedVendorNames = failedVendors.map(v => vendorDisplayNames[v] || v).join(", ");
                Snackbar.showWarningSnackbar(`No rate index results found for vendor(s): ${failedVendorNames}`);
            }
        }
    }

        private changeLabelDisplayType(labelName: Label, vendorURL: string, setDisplayType: boolean) {
        if (setDisplayType) {
            labelName.displayType = DisplayType.LINK;
            labelName.link = vendorURL;
        } else {
            labelName.displayType = null;
            labelName.link = vendorURL;
        }
    }

    private resetButtons() {
        this.buttonFetchOtherResults.caption = 'Get Other Rates';
        this.buttonFetchOtherResults.busy = false;
        this.buttonFetchOtherResults.imageName = null;
        this.buttonCalculate.busy = false;
        this.buttonCalculate.backgroundColor = "primary";
        this.buttonCalculate.color = "McLeodWhite";
        this.enableButtons(true);
        // this.buttonQuote.enabled = false;
    }

    private showMilesAndTollAmount(response: any, layoutDistanceCalculator: DistanceCalculatorOverview) {

        const lowerValue = "A toll amount in blue indicates a lower or equal toll amount.";
        const higherValue = "A toll amount in grey indicates a higher toll amount.";
        let quickInfoValueShort;
        let quickInfoValuePractical;

        const shortTollAmount = response?.short_toll_amount;
        layoutDistanceCalculator.labelShortMilesCalculateValue.text = response?.short_mileage;
        if (shortTollAmount) {
            const amount = shortTollAmount.amount;
            layoutDistanceCalculator.labelShortTollAmntCalculateValue.text = shortTollAmount.symbol + amount.toFixed(2);
        }
        const colorShort = response?.short_toll_amount_color;
        layoutDistanceCalculator.labelShortTollAmntCalculateValue.color = colorShort;

        const practicalTollAmount = response?.practical_toll_amount;
        layoutDistanceCalculator.labelPracticalMilesCalculateValue.text = response?.practical_mileage;
        if (practicalTollAmount) {
            const amount = practicalTollAmount.amount;
            layoutDistanceCalculator.labelPracticalTollAmntCalculateValue.text = practicalTollAmount.symbol + amount.toFixed(2);
        }
        const colorPractical = response?.practical_toll_amount_color;
        layoutDistanceCalculator.labelPracticalTollAmntCalculateValue.color = colorPractical;

        if (colorShort === "default.dark") {
            quickInfoValueShort = higherValue;
        }
        if (colorShort === "primary") {
            quickInfoValueShort = lowerValue;
        }

        if (colorPractical === "default.dark") {
            quickInfoValuePractical = higherValue;
        }
        if (colorPractical === "primary") {
            quickInfoValuePractical = lowerValue;
        }

        const quickInfoShort = new Label({ id: "hoverIdTwelve", text: quickInfoValueShort, rowBreak: true });
        const quickInfoPractical = new Label({ id: "hoverIdTwelve", text: quickInfoValuePractical, rowBreak: true });

        layoutDistanceCalculator.labelShortTollAmntCalculateValue.tooltipDynamicContent = quickInfoShort;
        layoutDistanceCalculator.labelPracticalTollAmntCalculateValue.tooltipDynamicContent = quickInfoPractical;
    }

    private whenResponseNull(response: any, fetchOtherResults: boolean) {
        if (response == null) {
            this.resetButtons();
            return;
        }
    }

    private validationLookUpModel(shortProfile: string, practicalProfile: string) {
        if (StringUtil.isEmptyString(shortProfile) && !this.layoutDistanceCalculationOverview.layoutShortDistanceRateConfig.visible) {
            CommonDialogs.showError("Short Distance Profile cannot be null or empty");
            return;
        }

        if (StringUtil.isEmptyString(practicalProfile) && !this.layoutDistanceCalculationOverview.layoutPracticalDistanceRateConfig.visible) {
            CommonDialogs.showError("Practical Distance Profile cannot be null or empty");
            return;
        }
    }

    private setEquipmentId() {
        const selectedEquipmentId = this.textboxTrailerType.text;
        let equipmentId = '';
        if (selectedEquipmentId === EQUIPMENT_TYPE_VAN.displayValue)
            equipmentId = EQUIPMENT_TYPE_VAN.value;
        else if (selectedEquipmentId == EQUIPMENT_TYPE_FLAT.displayValue)
            equipmentId = EQUIPMENT_TYPE_FLAT.value;
        else if (selectedEquipmentId == EQUIPMENT_TYPE_REEFER.displayValue)
            equipmentId = EQUIPMENT_TYPE_REEFER.value;
        else if (selectedEquipmentId == EQUIPMENT_TYPE_OTHER.displayValue)
            equipmentId = EQUIPMENT_TYPE_OTHER.value;
        return equipmentId;
    }

    resetMap() {
        this._lastMapResponse = null;
        this._lastMapRouteData = null;
        this.mapRoute.clearRoute();
        this.mapRoute.removeAllPins();
        this.labelMapinfo.text = "";
    }

    private cloneRouteDataForMap(routeData: any[]): any[] {
        if (!Array.isArray(routeData)) {
            return null;
        }

        const clonedRouteData: any[] = routeData.map(stop => ({ ...stop }));

        if (Array.isArray(routeData["trimble_coordinate_data"])) {
            clonedRouteData["trimble_coordinate_data"] = [...routeData["trimble_coordinate_data"]];
        }

        if (Array.isArray(routeData["route_stop_coords"])) {
            clonedRouteData["route_stop_coords"] = [...routeData["route_stop_coords"]];
        }

        if (Array.isArray(routeData["milemaker_polyline_data"])) {
            clonedRouteData["milemaker_polyline_data"] = [...routeData["milemaker_polyline_data"]];
        }

        return clonedRouteData;
    }

    clear() {
        this.mainDataSource.clear();
        this.sourceStops.clear();
        if (this.tableStops.data != null) {
            this.sourceStops.mode = DataSourceMode.UPDATE;
            this.tableStops.rows = [];
        }
        this.resetMap();
        this.initializeStopTable();
        this.displayData(null, null, 0);
        this.tableStops.data = [];
        this.mapRoute.visible = true;
        const totalRatesSummary = this.layoutTotalRatesSummary as RateIndexDataSummary;
        this.clearTotalOverviewLayout();
        this.clearTotalRatesSummaryLayout(totalRatesSummary);
        totalRatesSummary.setDefaultValues();
        this.clearButtonStyles();
        this._forceRefresh = true;
        this.currentRateIndexIdMap.clear();
        this.textboxMarginPercentage.text = this._userMarginPercentage;
        this.textboxTrailerType.setSelectedItemFromValue(this.textboxTrailerType.items[0].value);
        this.buttonQuote.enabled = false;
        this.clearStops.enabled = false;
        this.clearStops.color = "subtle.light";
        this.buttonFetchOtherResults.enabled = false;
        this.buttonFetchOtherResults.color = "subtle.light";
        this.buttonRouteDetails.enabled = false;
    }

    private clearButtonStyles() {
        this.buttonCalculate.enabled = true;
        this.buttonCalculate.busy = false;
        this.buttonCalculate.backgroundColor = "primary";
        this.buttonCalculate.color = "McLeodWhite";
        this.buttonFetchOtherResults.caption = 'Get Other Rates';
        this.buttonFetchOtherResults.enabled = true;
        this.buttonFetchOtherResults.imageName = null;
    }

    private clearTotalRatesSummaryLayout(totalRatesSummary: RateIndexDataSummary) {
        totalRatesSummary.setDefaultValues();
        this.resetTheLabelDisplayType(totalRatesSummary);
    }

    private resetTheLabelDisplayType(totalRatesSummary: RateIndexDataSummary) {
        this.changeLabelDisplayType(totalRatesSummary.labelMPactBuyRateHeader as Label, null, false);
        this.changeLabelDisplayType(totalRatesSummary.labelMPactSellRateHeader as Label, null, false);
        this.changeLabelDisplayType(totalRatesSummary.labelScacBuyRateHeader as Label, null, false);
        this.changeLabelDisplayType(totalRatesSummary.labelScacSellRateHeader as Label, null, false);
        this.changeLabelDisplayType(totalRatesSummary.labelDATSpotRateHeader as Label, null, false);
        this.changeLabelDisplayType(totalRatesSummary.labelDATContractRateHeader as Label, null, false);
        this.changeLabelDisplayType(totalRatesSummary.labelFreightWavesRateHeader as Label, null, false);
        this.changeLabelDisplayType(totalRatesSummary.labelTruckstopPostedRateHeader as Label, null, false);
        this.changeLabelDisplayType(totalRatesSummary.labelTruckstopPaidRateHeader as Label, null, false);
    }

    private clearTotalOverviewLayout() {
        const layoutDistanceCalculator = this.layoutDistanceCalculationOverview as DistanceCalculatorOverview;
        layoutDistanceCalculator.sourceDistanceControl.clear();
        layoutDistanceCalculator.sourceDistanceControl.search();

        // If custom distance options are configured do not reset distance profiles
        if (!this.layoutDistanceCalculationOverview.isShortConfigExpanded()) {
            const lookupShortProfile = layoutDistanceCalculator.sourceDistanceControl.activeRow?.get("short_dist_profile");
            layoutDistanceCalculator.textboxShortDistProfile.text = lookupShortProfile;
        }

        if (!this.layoutDistanceCalculationOverview.isPracticalConfigExpanded()) {
            const lookupPracticalProfile = layoutDistanceCalculator.sourceDistanceControl.activeRow?.get("prac_dist_profile");
            layoutDistanceCalculator.textboxPracticalProfile.text = lookupPracticalProfile;
        }

        layoutDistanceCalculator.labelShortMilesCalculateValue.text = "---";
        layoutDistanceCalculator.labelShortTollAmntCalculateValue.text = "---";
        layoutDistanceCalculator.labelShortTollAmntCalculateValue.color = "#252525";
        layoutDistanceCalculator.labelPracticalMilesCalculateValue.text = "---";
        layoutDistanceCalculator.labelPracticalTollAmntCalculateValue.text = "---";
        layoutDistanceCalculator.labelPracticalTollAmntCalculateValue.color = "#252525";

        // Remove HoverToDiscover
        layoutDistanceCalculator.labelShortTollAmntCalculateValue.allowTooltip = false;
        layoutDistanceCalculator.labelPracticalTollAmntCalculateValue.allowTooltip = false;
    }

    /** This is an event handler for the onContentsChanged event of tableStops.  */
    tableStopsOnContentsChanged(event: TableContentsChangedEvent) {
        this.currentRateIndexIdMap.clear();
        const action = event?.getAction();
        if (TableAction.DELETE === action) {
            if (this.calculationDisplayed && this.validateStops()) {
                this.tableStops.enabled = false;
                this.buttonCalculateOnClick();
            }
        }
    }

    /** This is an event handler for the onRowDisplay event of tableStops.  */
    tableStopsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();

        const locationDistanceCalc = tableRow.findComponentById("locationDistanceCalc") as Location;
        locationDistanceCalc.googlePlacesProps = {
            enableSearch: getDispatchControlBoolean("enable_google_places"),
            createLocations: false,
            customerId: "", doBeforeGoogleLocationCreated: null, doAfterGoogleLocationCreated: null
        };

        locationDistanceCalc.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true)
                this.locationDistCalcOnLookupSelection(event);
            if (tableRow.mode == TableRowMode.QUICK_ADD) {
                tableRow.persistQuickAddRow();
            }
            this.setDraggableRows();
        });

        locationDistanceCalc.setProps({ required: tableRow.mode != TableRowMode.QUICK_ADD });

        if (tableRow.mode == TableRowMode.QUICK_ADD) {
            locationDistanceCalc.textCombined.placeholder = "Add another stop...";
        }

        this.setDraggableRows();
        this.setupDeleteForRows();
    }

    /** Stops are only draggable when two or more stops exist  */
    private setDraggableRows() {
        let stopCount = 0;
        this.tableStops.allRows.forEach(row => {
            const cityId = row.data.get("city_id");
            if (cityId !== undefined) {
                stopCount++;
            }
            row.showDragHandle()
        });

        if (stopCount <= 1) {
            this.tableStops.allRows.forEach(row => {
                row.hideDragHandle();
            });
        }
    }

    private locationDistCalcOnLookupSelection(event: LookupModelSelectionEvent) {
        const locationTextCombined = event.target;
        const selectedRow = event.selection;
        const tableRow: TableRow = TableRow.getContainingTableRow(locationTextCombined);
        tableRow.data.setValues(selectedRow.data);

        if (tableRow.mode != TableRowMode.QUICK_ADD) {
            if (this.currentRateIndexIdMap != undefined && this.currentRateIndexIdMap.size != 0) {
                this.currentRateIndexIdMap.clear();
            }
            if (this.calculationDisplayed)
                this.buttonCalculateOnClick();
        }
    }

    /** This is an event handler for the onClick event of buttonCalculate.  */
    buttonCalculateOnClick() {
        if (this.textboxMarginPercentage.validateSimple(true, true) == false) {
            this.textboxMarginPercentage.focus();
        } else {
            const stopData = this.sourceStops.data.map(value => value.data);
            this.enableButtons(false);
            this.setShowAllRatesBoolean = false;
            this.buttonCalculate.busy = true;
            this.buttonCalculate.backgroundColor = "subtle.lightest";
            this.buttonCalculate.color = "default.lighter";

            this.calculate(stopData, this.getShowAllRatesBoolean, this.rateRetrievalList);
        }
    }

    private enableButtons(value: boolean) {
        this.buttonCalculate.enabled = value;
        this.buttonFetchOtherResults.enabled = value;
        this.tableStops.enableAllRowComponents(value);
        this.clearStops.enabled = value;
        this.buttonFetchOtherResults.enabled = value;
        if (value) {
            this.clearStops.color = "primary";
            this.buttonFetchOtherResults.color = "primary";
        }
        this.textboxTrailerType.enabled = value;
        this.textboxMarginPercentage.enabled = value;
        this.switchExcludeFuelSurcharge.enabled = value;

        // If custom distance options are configured do not reset distance profiles
        if (!this.layoutDistanceCalculationOverview.isShortConfigExpanded()) {
            this.layoutDistanceCalculationOverview.textboxShortDistProfile.enabled = value;
        }

        if (!this.layoutDistanceCalculationOverview.isPracticalConfigExpanded()) {
            this.layoutDistanceCalculationOverview.textboxPracticalProfile.enabled = value;
        }

        this.enableShortProfileConfigButtons(value);
        this.enablePracticalProfileConfigButtons(value);
    }

    private enableShortProfileConfigButtons(value: boolean) {
        this.layoutDistanceCalculationOverview.panelProfile1Config.getRecursiveChildren().forEach(function (component) { component.enabled = value });
    }

    private enablePracticalProfileConfigButtons(value: boolean) {
        this.layoutDistanceCalculationOverview.panelProfile2Config.getRecursiveChildren().forEach(function (component) { component.enabled = value });
    }

    /** This is an event handler for the onClick event of button Fetch Other Results.  */
    buttonFetchResultsOnClick() {
        this.currentRateIndexIdMap.clear();
        this._forceRefresh = true;
        this.setShowAllRatesBoolean = true;
        this.fetchResults();
    }

    buttonFetchVendorResultsOnClick(showAllRates: boolean = true, forceRefresh: boolean = true) {
        this.setShowAllRatesBoolean = showAllRates;
        this._forceRefresh = forceRefresh;
        this.fetchResults();
    }

    fetchResults() {
        const stopData = this.sourceStops.data.map(value => value.data);
        this.enableButtons(false);
        this.buttonCalculate.backgroundColor = "subtle.lightest";
        this.buttonCalculate.color = "default.lighter";
        this.buttonFetchOtherResults.caption = 'Loading ...';
        this.buttonFetchOtherResults.busy = true;
        this.buttonFetchOtherResults.imageName = ImageName.lock;
        this.calculate(stopData, this.getShowAllRatesBoolean, null);
    }

    buttonQuoteOnClick(event: ClickEvent) {
        const stopData = this.sourceStops.data.map(value => value.data);
        const trailerType = this.setEquipmentId();
        new CrudDecorator({
            mode: DataSourceMode.ADD,
            layout: Layout.getLayout("lme/powerbroker/QuickQuote") as QuickQuote,
            headerProps: { showClose: true },
            providedApiData: { stopData: this.sourceStops.data, orderData: { "equipment_type_id": trailerType, "_lookup_equipment_type_id": { id: trailerType } } }
        }).slideIn({ speed: 200 });

    }

    buttonRouteDetailsOnClick() {
        const hazmatType = this.getHazmatType();
        const source = this.layoutDistanceCalculationOverview.layoutPracticalDistanceRateConfig.sourceDistanceProfile.activeRow?.get("source");
        const type = this.layoutDistanceCalculationOverview.layoutPracticalDistanceRateConfig.sourceDistanceProfile.activeRow?.get("lookup_type");
        const method = this.layoutDistanceCalculationOverview.layoutPracticalDistanceRateConfig.sourceDistanceProfile.activeRow?.get("method");

        DistanceRouteDetails.showSlideout({
            sourceStops: this.sourceStops,
            source: source,
            type: type,
            method: method,
            hazmatType: hazmatType
        });

    }

    getHazmatType() {
        const { checkboxHazmatRouting, checkboxIntelliHazmatRouting, textboxMileMakerHazmatTypes } = this.layoutDistanceCalculationOverview.layoutPracticalDistanceRateConfig;
        return (checkboxHazmatRouting.checked || checkboxIntelliHazmatRouting.checked || textboxMileMakerHazmatTypes.selectedItems?.length > 0)
            ? HAZMAT_GENERAL
            : HAZMAT_NONE;
    }


    public get getShowAllRatesBoolean(): boolean {
        return this._showAllTotalRates;
    }

    public set setShowAllRatesBoolean(value: boolean) {
        this._showAllTotalRates = value;
    }

    private showMap() {
        if (this.distCalcMapVendor == GoogleMapVendor) {
            const googleApiKey = MapSettings.get()?.google_api_key;
            if (googleApiKey) {
                this.mapRoute.visible = true;
            }
        }
        else if (this.distCalcMapVendor == TrimbleAlkMapVendor) {
            this.mapRoute.visible = true;
        }
        else {
            this.mapRoute.visible = false;
        }
        this.mapRoute.fitPins()
    }

    private populateMap(response: any, routeData: any[]) {
        if (!response)
            return;

        this.labelMapinfo.text = "";

        const practicalDistanceVendor = response.practical_distance_vendor;
        this.isPcMilerOnPremVendor = [PCMILER_ON_PREM, MULTI_VERSION_PCMILER].includes(practicalDistanceVendor);
        this.isPcMilerWebDistanceVendor = PCMILER_WEB === practicalDistanceVendor;
        this.isMileMakerVendor = [MILEMAKER].includes(practicalDistanceVendor);

        if (this.isPcMilerWebDistanceVendor || this.isPcMilerOnPremVendor) {
            routeData["trimble_coordinate_data"] = response?.trimble_route;
            routeData["route_stop_coords"] = response.route_stop_coords;
            if (this.distCalcMapVendor == GoogleMapVendor
                && this.isPcMilerOnPremVendor) {
                if (response?.trimble_route != null) {
                    this.labelMapinfo.text = "Red - Trimble truck route       Blue - Google non-truck route";
                    this.labelMapinfo.visible = true;
                }
            }
        } else if (this.distCalcMapVendor == GoogleMapVendor && this.isMileMakerVendor) {
            routeData["milemaker_polyline_data"] = response?.milemaker_route;
            this.labelMapinfo.text = "Red - MileMaker truck route       Blue - Google non-truck route";
            this.labelMapinfo.visible = true;
        } else if (this.distCalcMapVendor == GoogleMapVendor) {
            this.labelMapinfo.text = "This is not a truck-based route";
            this.labelMapinfo.visible = true;
        }

        if (response) {
            this.updateMap(routeData);
        }
    }

    private updateMap(routeData: any[]) {
        this.sortRoute(routeData);

        if (this.distCalcMapVendor == GoogleMapVendor) {
            // Create vendor info to pass to map
            const vendorInfo = {
                isMileMaker: this.isMileMakerVendor,
                isPcMiler: this.isPcMilerOnPremVendor || this.isPcMilerWebDistanceVendor,
                isPcMilerWeb: this.isPcMilerWebDistanceVendor,
                source: this.isPcMilerWebDistanceVendor ? PCMILER_WEB :
                       (this.isPcMilerOnPremVendor ? PCMILER_ON_PREM :
                       (this.isMileMakerVendor ? MILEMAKER : '')),
                routeConfig: {
                    showGoogleToggle: true,
                    showMileMakerToggle: this.isMileMakerVendor,
                    showTrimbleToggle: this.isPcMilerOnPremVendor
                }
            };

            // Create route IDs with vendor info
            const googleRouteId = JSON.stringify({
                routeId: "google_route",
                vendorInfo
            });

            const trimbleRouteId = JSON.stringify({
                routeId: "pcmiler_route",
                vendorInfo
            });

            const milemakerRouteId = JSON.stringify({
                routeId: "milemaker_route",
                vendorInfo
            });

            // Create Google route with vendor info
            this.mapRoute.createRoute(
                routeData,
                RouteType.Google,
                false,               // Don't hide markers
                googleRouteId,       // Include vendor info
                "#0000FF",           // Blue color for Google routes
                false,               // Show proposed route
                false                 // False = Show toggle buttons
            );

            if (this.isPcMilerOnPremVendor) {
                if (routeData['trimble_coordinate_data'] != null) {
                    // Create PC*Miler On Prem route with vendor info in route ID
                    this.mapRoute.createRoute(
                        routeData,
                        RouteType.Trimble,
                        false,               // Don't hide markers
                        trimbleRouteId,      // Include vendor info
                        "#FF0000",           // Red color for PC*Miler On Prem routes
                        true,                // Show proposed route
                        false                 // False = Show toggle buttons
                    );
                }
            }

            // Don't create PC*Miler Web routes on Google maps (unlike PC*Miler On Prem)
            // We're letting GoogleMap.ts handle this prevention, but we skip the creation here too for consistency

            if (this.isMileMakerVendor && routeData["milemaker_polyline_data"] && routeData["milemaker_polyline_data"].length > 0) {
                // Create MileMaker route with vendor info in route ID
                this.mapRoute.createRoute(
                    routeData,
                    RouteType.MileMaker,
                    false,               // Don't hide markers
                    milemakerRouteId,    // Include vendor info
                    "#FF0000",           // Red color for MileMaker routes
                    true,                // Show proposed route
                    true                 // Disable toggle buttons
                );
            }
        } else {
            // Create vendor info for non-Google maps
            const nonGoogleVendorInfo = {
                isMileMaker: this.isMileMakerVendor,
                isPcMiler: this.isPcMilerOnPremVendor || this.isPcMilerWebDistanceVendor,
                isPcMilerWeb: this.isPcMilerWebDistanceVendor,
                source: this.isPcMilerWebDistanceVendor ? PCMILER_WEB :
                       (this.isPcMilerOnPremVendor ? PCMILER_ON_PREM :
                       (this.isMileMakerVendor ? MILEMAKER : ''))
            };

            // Create route ID with vendor info
            const nonGoogleRouteId = JSON.stringify({
                routeId: "trimble_route",
                vendorInfo: nonGoogleVendorInfo
            });

            // If we have PC*Miler data, create those routes on the map
            if (this.isPcMilerOnPremVendor || this.isPcMilerWebDistanceVendor) {
                if (routeData['route_stop_coords'] != null && routeData['route_stop_coords'].length > 0) {
                    this.mapRoute.createRouteFromCoordArray(routeData, RouteType.Trimble);
                } else {
                    this.mapRoute.createRoute(
                        routeData,
                        RouteType.Trimble,
                        false,
                        nonGoogleRouteId,    // Include vendor info
                        "#00AA00",           // Green color for PCMiler routes
                        false,
                        true                 // Disable toggle buttons
                    );
                }
            } else {
                // Create route without toggle buttons for non-Google vendors
                this.mapRoute.createRoute(
                    routeData,
                    undefined,
                    false,
                    nonGoogleRouteId,    // Include vendor info
                    undefined,
                    true,
                    true
                );
            }

            const isTrafficEnabled = LMEUserSettings.get()?.trimble_map_traffic;
            if (isTrafficEnabled === "Y") {
                this.mapRoute.setStopZoomLevel(isTrafficEnabled, routeData);
            }
        }
    }

    private sortRoute(routeData: any[]) {
        routeData.sort((a, b) => {
            return a.order_sequence - b.order_sequence;
        });
    }

    stopsTableAfterTableRowMove(event: TableRowMoveEvent) {
        this.handleTableRowChange(event?.oldIndex, event?.newIndex);
    }

    private handleTableRowChange(oldRowIndex: number, newRowIndex: number) {
        this.currentRateIndexIdMap.clear();
        if (oldRowIndex === undefined || newRowIndex === undefined)
            return;
        this.setupDeleteForRows();
        this.buttonCalculateOnClick();
    }

    private setupDeleteForRows() {
        this.tableStops.rows.forEach(row=> {
            row.allowDelete = this.tableStops.rowCount > 2;
        });
    }

    private setMarginPercentage(): string {
        const marginPercentage = this.textboxMarginPercentage.text;
        return marginPercentage;
    }

    textboxMarginPercentageOnBlur(event: BlurEvent) {
        if (StringUtil.isEmptyString(this.textboxMarginPercentage.text))
            this.textboxMarginPercentage.text = "0";

        const margin = Number(this.textboxMarginPercentage.text);
        if (this.textboxMarginPercentage.validateSimple(true, true) == false || isNaN(margin) || margin < 0) {
            this.textboxMarginPercentage.required = true;
            this.textboxMarginPercentage.focus();
            this.textboxMarginPercentage.validationWarning = "Please enter a positive number greater than or equal to zero";
            this.buttonCalculate.enabled = false;
            this.buttonFetchOtherResults.enabled = false;
        } else {
            this.textboxMarginPercentage.required = false;
            this.buttonCalculate.enabled = true;
            this.buttonFetchOtherResults.enabled = true;
            this.textboxMarginPercentage.validationWarning = null;
            this._forceRefresh = false;
            if (this.currentRateIndexIdMap != undefined && this.currentRateIndexIdMap.size != 0 && event.changedWhileFocused) {
                this.buttonFetchVendorResultsOnClick(this._showAllTotalRates, false);
            }
        }
    }

    switchExcludeFuelSurchargeOnClick() {
        if (this.currentRateIndexIdMap != undefined && this.currentRateIndexIdMap.size != 0)
            this.buttonFetchVendorResultsOnClick(this._showAllTotalRates, false);
    }

    public setCurrentRateIndexId(vendor: string, id: string) {
        this.currentRateIndexIdMap.set(vendor, id);
    }

    public get cachedShortProfile(): string {
        return this._cachedShortProfile;
    }

    public set cachedShortProfile(value: string) {
        this._cachedShortProfile = value;
    }

    public get cachedPracticalProfile(): string {
        return this._cachedPracticalProfile;
    }

    public set cachedPracticalProfile(value: string) {
        this._cachedPracticalProfile = value;
    }

    public get cachedTrailerType(): string {
        return this._cachedTrailerType;
    }

    public set cachedTrailerType(value: string) {
        this._cachedTrailerType = value;
    }

    focusLocation(row: TableRow) {
        (row.findComponentById("locationDistanceCalc") as Location)?.textCombined?.focus();
    }

    private async initializeFieldsViaRateIndexControl(): Promise<void> {
        await this.setFieldsViaRateIndexControl();

        const totalRatesSummary = this.layoutTotalRatesSummary as RateIndexDataSummary;
        totalRatesSummary.hideColumns();
        const hasAutoDisplayRates = Array.isArray(this.rateRetrievalList) && this.rateRetrievalList.length > 0;
        if (!hasAutoDisplayRates) {
            return;
        }

        // Define configuration map for vendor rate type settings
        const rateTypeConfigs = {
            [RATE_VENDORS.TRUCKSTOP]: {
                'B': this._displayTruckstopPaidRate,
                'S': this._displayTruckstopPostedRate
            },
            [RATE_VENDORS.DAT]: {
                'C': this._displayDATContractRate,
                'S': this._displayDATSpotRate
            },
            [RATE_VENDORS.GREENSCREENS]: {
                'B': this._displayGreenscreensTargetRate,
                'S': this._displayGreenscreensNetworkRate
            },
            [RATE_VENDORS.MPACT]: {
                'B': this._displayMPactSellRate,
                'S': this._displayMPactSpotRate,
                'I': this._displayMPactInternalRate
            }
        };

        // Only initially show the vendors that are in the rateRetrievalList
        const rateTypes = ['S', 'B', 'C', 'N', 'T', 'I'];

        this.rateRetrievalList.forEach(vendor => {
            if (!vendor) {
                return;
            }

            rateTypes.forEach(rateType => {
                // Check if this vendor+rateType combination is disabled
                const vendorConfig = rateTypeConfigs[vendor];
                if (vendorConfig && rateType in vendorConfig && !vendorConfig[rateType]) {
                    return; // Skip disabled rate types
                }

                const [panelTotalRate, panelExpandable] = totalRatesSummary.getPanelPerVendorAndRateType(vendor, rateType);
                if (panelTotalRate && panelExpandable) {
                    panelTotalRate.visible = true;
                    panelExpandable.visible = true;
                }
            });
        });
    }

    public async setFieldsViaRateIndexControl(): Promise<void> {
        try {
            const data = await this.sourceRateIndexControl.search();
            this.rateRetrievalList = [];

            if (data && data.modelRows) {
                data.modelRows.forEach((row: any) => {
                    if (row?._data?.licensed_and_setup) {
                        if (!StringUtil.isEmptyString(row?._data?.auto_rate_retrieval) && StringUtil.equalsIgnoreCase(row?._data?.auto_rate_retrieval, "Y")) {
                            this.rateRetrievalList.push(row?._data.vendor);
                        }
                    }
                    if (row?._data?.dat_rateview_enabled && StringUtil.equalsIgnoreCase(row?._data?.vendor, RATE_VENDORS.DAT)) {
                        const showEscalationOptions = StringUtil.equalsIgnoreCase(row?.data?.dat_rateview_enabled && row?.data?.enabled_escalation_option, "Y");
                        if (showEscalationOptions) {
                            this.textboxEscalationType.visible = showEscalationOptions;
                            this.textboxEscalationType.required = showEscalationOptions;
                            this._escalationType = row?._data?.escalation_type;
                            this._areaType = row?._data?.specific_area_type;
                            this._timeFrameSpot = row?._data?.specific_time_frame;
                            this._timeFrameContract = row?._data?.specific_timeframe_contract;
                            this._isSpotRate = StringUtil.equalsIgnoreCase(row?._data?.spot_rate_type, "Y");
                            this._isContractRate = StringUtil.equalsIgnoreCase(row?._data?.contract_rate_type, "Y");
                            this.updateEscalationComboValues(this._isSpotRate, this._isContractRate, this._escalationType, this._areaType, this._timeFrameSpot, this._timeFrameContract);
                            this.updateEscalationFieldsVisibility(this._escalationType);
                        }
                        this._displayDATContractRate = StringUtil.equalsIgnoreCase(row?._data?.contract_rate_type, "Y");
                        this._displayDATSpotRate = StringUtil.equalsIgnoreCase(row?._data?.spot_rate_type, "Y");
                    } else if (StringUtil.equalsIgnoreCase(row?._data?.vendor, RATE_VENDORS.TRUCKSTOP)) {
                        this._displayTruckstopPaidRate = StringUtil.equalsIgnoreCase(row?._data?.billed_rate_type, "Y");
                        this._displayTruckstopPostedRate = StringUtil.equalsIgnoreCase(row?._data?.spot_rate_type, "Y");
                    } else if (StringUtil.equalsIgnoreCase(row?._data?.vendor, RATE_VENDORS.GREENSCREENS)) {
                        this._displayGreenscreensTargetRate = StringUtil.equalsIgnoreCase(row?._data?.billed_rate_type, "Y");
                        this._displayGreenscreensNetworkRate = StringUtil.equalsIgnoreCase(row?._data?.spot_rate_type, "Y");
                    } else if (StringUtil.equalsIgnoreCase(row?._data?.vendor, RATE_VENDORS.MPACT)) {
                        this._displayMPactSellRate = StringUtil.equalsIgnoreCase(row?._data?.billed_rate_type, "Y");
                        this._displayMPactSpotRate = StringUtil.equalsIgnoreCase(row?._data?.spot_rate_type, "Y");
                        this._displayMPactInternalRate = StringUtil.equalsIgnoreCase(row?._data?.internal_rate_type, "Y");
                    }
                });
            }
        } catch (error) {
            console.error("Error retrieving rate retrieval list:", error);
        }
    }


    private updateEscalationFieldsVisibility(escalationType: string) {
        if (!StringUtil.isEmptyString(escalationType)) {
            const isSpecificTimeFrameVisible = ["S", "M", "SA"].some(type =>
                StringUtil.equalsIgnoreCase(type, escalationType)
            );
            const isSpecificAreaTypeVisible = ["S", "M", "SS"].some(type =>
                StringUtil.equalsIgnoreCase(type, escalationType)
            );
            this.textboxTimeFrame.visible = isSpecificTimeFrameVisible && this._isSpotRate;
            this.textboxTimeFrame.required = isSpecificTimeFrameVisible;
            this.textboxTimeFrameContract.visible = isSpecificTimeFrameVisible && this._isContractRate;
            this.textboxTimeFrameContract.required = isSpecificTimeFrameVisible;
            this.textboxAreaType.visible = isSpecificAreaTypeVisible;
            this.textboxAreaType.required = isSpecificAreaTypeVisible;

            if (!isSpecificTimeFrameVisible) {
                this._timeFrameSpot = "";
                this._timeFrameContract = "";
            }
            if (!isSpecificAreaTypeVisible) {
                this._areaType = "";
            }
        }
    }

    private updateEscalationComboValues(isSpotRate: boolean, isContractRate: boolean, escalationType: string, areaType: string, timeFrame: string, timeFrameContract: string) {
        const isSpotChecked = isSpotRate;
        const isContractChecked = isContractRate;

        this.textboxAreaType.items = this._areaTypeOptions;
        this.textboxEscalationType.items = this._escalationTypeOptions;
        this.textboxTimeFrame.items = this._timeFrameSpotOptions;
        this.textboxTimeFrameContract.items = this._timeFrameContractOptions;

        if (!StringUtil.isEmptyString(escalationType)) {
            this.textboxEscalationType.setSelectedItemFromValue(escalationType);
        } else {
            this.textboxEscalationType.setSelectedItemFromValue(this.textboxEscalationType.items[0].value);
        }
        if (!StringUtil.isEmptyString(timeFrame)) {
            this.textboxTimeFrame.setSelectedItemFromValue(timeFrame);
            this._timeFrameSpot = timeFrame;
        } else {
            this.textboxTimeFrame.setSelectedItemFromValue(this.textboxTimeFrame.items[0].value);
            this._timeFrameSpot = this.textboxTimeFrame.items[0].value;
        }

        if (!StringUtil.isEmptyString(timeFrameContract)) {
            this.textboxTimeFrameContract.setSelectedItemFromValue(timeFrameContract);
            this._timeFrameContract = timeFrameContract;
        } else {
            this.textboxTimeFrameContract.setSelectedItemFromValue(this.textboxTimeFrameContract.items[0].value);
            this._timeFrameContract = this.textboxTimeFrameContract.items[0].value;
        }

        if (!StringUtil.isEmptyString(areaType)) {
            this.textboxAreaType.setSelectedItemFromValue(areaType);
            this._areaType = areaType;
        } else {
            this.textboxAreaType.setSelectedItemFromValue(this.textboxAreaType.items[0].value);
            this._areaType = this.textboxAreaType.items[0].value;
        }
    }

    private textboxEscalationTypeOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const textboxEscalationType = event.target as Textbox;
            const selectedItem = textboxEscalationType.selectedItem;
            if (selectedItem) {
                const escalationType = selectedItem.value;
                this._escalationType = escalationType;
                this.updateEscalationFieldsVisibility(escalationType);
                this.updateEscalationComboValues(this._isSpotRate, this._isContractRate, this._escalationType, this._areaType, this._timeFrameSpot, this._timeFrameContract);

            }
        }
    }

    private textboxTimeFrameOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const textboxTimeFrame = event.target as Textbox;
            const selectedItem = textboxTimeFrame.selectedItem;
            if (selectedItem) {
                const timeFrame = selectedItem.value;
                this._timeFrameSpot = timeFrame;
            }
        }
    }

    private textboxTimeFrameContractOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const textboxTimeFrameContract = event.target as Textbox;
            const selectedItem = textboxTimeFrameContract.selectedItem;
            if (selectedItem) {
                const timeFrame = selectedItem.value;
                this._timeFrameContract = timeFrame;
            }
        }
    }

    private textboxAreaTypeOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const textboxAreaType = event.target as Textbox;
            const selectedItem = textboxAreaType.selectedItem;
            if (selectedItem) {
                const areaType = selectedItem.value;
                this._areaType = areaType;
            }
        }
    }

}

export async function fetchRateIndexResults(calculator: DistanceRateIndexCalculator, vendor: string, routeData: any[], equipmentId: string, fetchOtherResults: boolean, layoutTotalRates: RateIndexDataSummary, mileage: string, marginPercentage: string, excludeFuelSurcharge: boolean, tolls: string, profile: string, currentRateId: string, forceRefresh: boolean, escalationType: string, timeFrameSpot: string, timeFrameContract: string, areaType: string): Promise<boolean> {
    try {
        const response = await Api.search("lme/dispatch/rate-index-results-data",
            {
                stops: routeData,
                equipment_id: equipmentId,
                show_all_total_rates: fetchOtherResults,
                record_id: currentRateId,
                force_refresh: forceRefresh,
                run_type: 3,
                mileage: mileage,
                tolls: tolls,
                margin_percentage: marginPercentage,
                exclude_fuel_surcharge: excludeFuelSurcharge,
                vendor: vendor,
                escalation_type: escalationType,
                area_type: areaType,
                time_frame_spot: timeFrameSpot,
                time_frame_contract: timeFrameContract
            });

        const data = response?.data[0];
        log.debug("Results for vendor: " + vendor);

        // Check if vendors_data exists and is not empty
        if (!data?.vendors_data || data.vendors_data.length === 0) {
            log.debug(`No rate index data returned for ${vendor}`);
            return false;
        }

        const scacCode = data.show_company_scac;
        for (const vendorData of data.vendors_data) {
            layoutTotalRates.populateRateIndexResultData(vendorData, fetchOtherResults, scacCode, profile);
            calculator.setCurrentRateIndexId(vendor, vendorData?.movement_id);
        }

        return true;
    } catch (error) {
        log.error(`Error fetching rate index results for ${vendor}:`, error);
        return false;
    }
}
